import { type ReactNode, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Ban, CheckCircle, Download, Globe, RefreshCw, Shield, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fetchCompanyCenter, runCompanyOperation, type CompanyCenter } from "@/lib/adminCompanies";
import { toast } from "sonner";

const actionGroups = {
  team: ["add_user", "remove_user", "disable_user", "enable_user", "reset_password", "force_logout", "resend_invitation", "transfer_ownership", "view_user_activity"],
  billing: ["upgrade_package", "downgrade_package", "grant_credits", "remove_credits", "override_limits", "reset_monthly_usage", "force_renewal", "cancel_subscription", "reactivate_subscription"],
  websites: ["create_website", "disable_website", "delete_website", "rebuild_website", "force_sync", "repair_configuration"],
  articles: ["bulk_delete_articles", "bulk_publish_articles", "recalculate_statistics", "clear_failed_queue"],
  sources: ["rerun_source", "disable_source", "delete_source", "view_source_errors"],
  credits: ["grant_credits", "remove_credits", "reset_credits", "export_usage"],
  integrations: ["test_connection", "reconnect", "view_integration_logs"],
  templates: ["edit_template", "reset_template_default", "clone_templates", "export_templates", "import_templates"],
  utilities: ["impersonate_company_owner", "login_as_user", "export_backup", "restore_backup", "duplicate_company", "clone_configuration", "clone_integrations", "repair_company_data", "rebuild_permissions", "verify_isolation", "run_health_check", "run_security_audit", "run_billing_audit", "run_credit_audit"],
};

type ActionFormState = {
  user_id: string;
  invitation_id: string;
  email: string;
  role: string;
  package_id: string;
  amount: string;
  limits_json: string;
  name: string;
  domain: string;
  website_id: string;
  article_selector: string;
  source_id: string;
  provider: string;
  template_id: string;
  fields_json: string;
  source_company_id: string;
  templates_json: string;
  backup_json: string;
};

const emptyActionForm: ActionFormState = {
  user_id: "",
  invitation_id: "",
  email: "",
  role: "viewer",
  package_id: "",
  amount: "100",
  limits_json: "{\"monthly_limit\":0,\"daily_limit\":0}",
  name: "",
  domain: "",
  website_id: "",
  article_selector: "status:draft",
  source_id: "",
  provider: "Stripe",
  template_id: "",
  fields_json: "{\"is_enabled\":true}",
  source_company_id: "",
  templates_json: "[]",
  backup_json: "{}",
};

export default function CompanyDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [disableText, setDisableText] = useState("");
  const [deleteText, setDeleteText] = useState("");
  const [secondConfirm, setSecondConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [actionForm, setActionForm] = useState<ActionFormState>(emptyActionForm);

  const query = useQuery({
    queryKey: ["company-center", id],
    queryFn: () => fetchCompanyCenter(id!),
    enabled: !!id,
  });

  const operation = useMutation({
    mutationFn: ({ op, payload }: { op: string; payload?: Record<string, unknown> }) => runCompanyOperation(id!, op, payload),
    onSuccess: (data) => {
      if (data.export) exportJson(`${data.operation ?? "company-export"}.json`, data.export);
      if (data.report) exportJson(`${data.operation ?? "company-report"}.json`, data.report);
      toast[data.success === false ? "warning" : "success"](data.message || `${titleCase(data.operation ?? "operation")} recorded`);
      queryClient.invalidateQueries({ queryKey: ["company-center", id] });
      queryClient.invalidateQueries({ queryKey: ["companies-admin"] });
      queryClient.invalidateQueries({ queryKey: ["companies-platform-dashboard"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Operation failed"),
  });

  const center = query.data;
  const company = center?.company;
  const owner = center?.users.find((user) => user.user_id === company?.owner_user_id);
  const pendingInvitations = center?.invitations.filter((invite) => !invite.accepted_at) ?? [];
  const runAction = (op: string) => {
    if (!center) return;
    if (actionRequiresDialog(op)) {
      setActionForm(createInitialActionForm(op, center));
      setPendingAction(op);
      return;
    }
    operation.mutate({ op, payload: {} });
  };
  const submitPendingAction = () => {
    if (!center || !pendingAction) return;
    const payload = buildActionPayload(pendingAction, actionForm);
    if (payload === null) return;
    operation.mutate(
      { op: pendingAction, payload },
      { onSuccess: () => setPendingAction(null) },
    );
  };

  if (query.isLoading) return <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Loading company center...</div>;
  if (query.error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="max-w-xl border-destructive/40">
          <CardContent className="space-y-4 pt-6">
            <div className="flex items-center gap-2 font-medium text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Company details failed to load
            </div>
            <p className="text-sm text-muted-foreground">{query.error instanceof Error ? query.error.message : String(query.error)}</p>
            <p className="text-xs text-muted-foreground">This page requires the latest admin-company-actions Edge Function with get_company_center support.</p>
            <Button variant="outline" onClick={() => navigate("/companies")}>Back to companies</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!center || !company) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="max-w-md">
          <CardContent className="space-y-3 pt-6">
            <p className="font-medium">Company not found</p>
            <Button variant="outline" onClick={() => navigate("/companies")}>Back to companies</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 px-6 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/companies")}><ArrowLeft className="h-4 w-4" /></Button>
            {company.logo_url ? <img src={company.logo_url} alt="" className="h-10 w-10 rounded-md object-cover" /> : <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10"><Globe className="h-5 w-5 text-primary" /></div>}
            <div>
              <h1 className="text-lg font-semibold text-foreground">{company.name}</h1>
              <p className="text-xs text-muted-foreground">{company.slug} · {company.id}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <HealthBadge level={center.health.level} score={center.health.score} />
            <StatusBadge status={company.status ?? "active"} />
            <Button
              size="sm"
              variant="outline"
              disabled={operation.isPending}
              onClick={() => runAction("run_health_check")}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${operation.isPending ? "animate-spin" : ""}`} />
              {operation.isPending ? "Running Check" : "Run Health Check"}
            </Button>
          </div>
        </div>
      </header>

      <main className="space-y-6 p-6">
        <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <InfoCard title="Company" rows={[
            ["Name", company.name],
            ["Slug", company.slug],
            ["Company ID", company.id],
            ["Creation Date", formatDate(company.created_at)],
            ["Last Update", formatDate(company.updated_at)],
            ["Status", company.status ?? "active"],
            ["Time Zone", company.time_zone ?? "Not configured"],
            ["Language", company.language ?? "Not configured"],
            ["Country", company.country ?? "Not configured"],
          ]} />
          <InfoCard title="Subscription" rows={[
            ["Package", company.subscription_plan_id ?? "Unassigned"],
            ["Subscription Status", center.subscription?.status ?? "No subscription"],
            ["Billing Status", center.subscription?.payment_status ?? "Unknown"],
            ["Renewal Date", formatDate(center.subscription?.renewal_date)],
            ["Trial End Date", formatDate(center.subscription?.trial_end)],
            ["Next Invoice", center.invoices[0]?.invoice_number ?? "None"],
            ["Payment Method", center.subscription?.stripe_customer_id ? "Stripe customer linked" : "Unknown"],
          ]} />
          <InfoCard title="Owner" rows={[
            ["Owner", owner?.full_name ?? "Unknown"],
            ["Contact Email", company.contact_email ?? owner?.email ?? "Unknown"],
            ["Admins", countRole(center.users, "admin")],
            ["Members", center.users.length],
            ["Pending Invitations", center.invitations.filter((invite) => !invite.accepted_at).length],
            ["Last Login", formatDate(owner?.last_login_at)],
          ]} />
          <Card>
            <CardHeader><CardTitle className="text-sm">Health Monitoring</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="text-3xl font-semibold">{center.health.score}%</div>
              {center.health.checks.map((check) => (
                <div key={check.key} className="flex items-center justify-between gap-3 text-sm">
                  <span>{check.label}</span>
                  {check.ok ? <CheckCircle className="h-4 w-4 text-green-600" /> : <AlertTriangle className="h-4 w-4 text-yellow-600" />}
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        <Tabs defaultValue="team" className="space-y-4">
          <TabsList className="flex h-auto flex-wrap justify-start">
            {["team", "billing", "websites", "articles", "sources", "credits", "integrations", "templates", "activity", "danger", "utilities"].map((tab) => (
              <TabsTrigger key={tab} value={tab}>{titleCase(tab)}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="team" className="space-y-4">
            <ActionBar actions={actionGroups.team} onRun={runAction} />
            <DataTable title="Users & Team Management" rows={center.users} columns={["full_name", "email", "role", "status", "last_login_at", "joined_at"]} />
            <PendingInvitationsTable
              rows={pendingInvitations}
              pending={operation.isPending}
              onResend={(invite) => operation.mutate({ op: "resend_invitation", payload: { invitation_id: invite.id, email: invite.email } })}
              onRemove={(invite) => operation.mutate({ op: "cancel_invitation", payload: { invitation_id: invite.id } })}
            />
          </TabsContent>

          <TabsContent value="billing" className="space-y-4">
            <ActionBar actions={actionGroups.billing} onRun={runAction} />
            <InfoCard title="Package & Subscription Management" rows={[
              ["Current Package", company.subscription_plan_id ?? "Unassigned"],
              ["Stripe Subscription", center.subscription?.stripe_subscription_id ?? "None"],
              ["Billing Status", center.subscription?.payment_status ?? "Unknown"],
              ["Next Invoice", center.invoices[0]?.invoice_number ?? "None"],
              ["Credits Included", center.subscription?.metadata?.ai_credits ?? "Unknown"],
              ["Limits", JSON.stringify(center.subscription?.manual_limit_overrides ?? {})],
            ]} />
            <DataTable title="Invoices" rows={center.invoices} columns={["invoice_number", "status", "total_amount", "amount_paid", "renewal_date", "created_at"]} />
          </TabsContent>

          <TabsContent value="websites" className="space-y-4">
            <ActionBar actions={actionGroups.websites} onRun={runAction} />
            <DataTable title="Website Management" rows={center.websites} columns={["name", "domain", "status", "provisioning_status", "updated_at", "article_count"]} />
          </TabsContent>

          <TabsContent value="articles" className="space-y-4">
            <StatsGrid stats={center.article_stats} />
            <ActionBar actions={actionGroups.articles} onRun={runAction} />
            <DataTable title="Articles & Publishing" rows={center.articles} columns={["title", "status", "published_at", "scheduled_at", "updated_at"]} />
          </TabsContent>

          <TabsContent value="sources" className="space-y-4">
            <StatsGrid stats={center.source_stats} />
            <ActionBar actions={actionGroups.sources} onRun={runAction} />
            <DataTable title="Sources & RSS Monitoring" rows={center.sources} columns={["topic", "source_type", "is_active", "last_status", "last_error", "last_fetched_at"]} />
          </TabsContent>

          <TabsContent value="credits" className="space-y-4">
            <StatsGrid stats={{ current_balance: center.credits.current_balance, lifetime_credits: center.credits.lifetime_credits, consumed_credits: center.credits.consumed_credits, ...center.credits.breakdown }} />
            <ActionBar actions={actionGroups.credits} onRun={runAction} />
            <DataTable title="Credit History" rows={center.credits.transactions} columns={["type", "amount", "description", "created_at"]} />
          </TabsContent>

          <TabsContent value="integrations" className="space-y-4">
            <ActionBar actions={actionGroups.integrations} onRun={runAction} />
            <DataTable title="Integration Status" rows={center.integrations.providers} columns={["provider", "status", "updated_at"]} />
            <DataTable title="Integration Logs" rows={center.integrations.logs} columns={["integration_name", "action", "status", "error_message", "created_at"]} />
          </TabsContent>

          <TabsContent value="templates" className="space-y-4">
            <ActionBar actions={actionGroups.templates} onRun={runAction} />
            <DataTable title="Email Templates" rows={center.templates.email} columns={["name", "template_key", "is_enabled", "updated_at"]} />
            <DataTable title="Invoice Templates" rows={center.templates.invoice} columns={["name", "template_key", "is_enabled", "is_default", "updated_at"]} />
            <DataTable title="Content Templates" rows={center.templates.content} columns={["name", "category", "is_active", "updated_at"]} />
          </TabsContent>

          <TabsContent value="activity" className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => exportCsv("company-logs.csv", center.logs)}><Download className="mr-2 h-4 w-4" />Export CSV</Button>
              <Button size="sm" variant="outline" onClick={() => exportCsv("company-logs.xls", center.logs)}>Export Excel</Button>
            </div>
            <DataTable title="Activity & Audit Logs" rows={center.logs} columns={["created_at", "action", "entity_type", "status", "user_id"]} />
          </TabsContent>

          <TabsContent value="utilities" className="space-y-4">
            <ActionBar actions={actionGroups.utilities} onRun={runAction} />
          </TabsContent>

          <TabsContent value="danger" className="space-y-4">
            <Card className="border-yellow-500/40">
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Ban className="h-4 w-4" />Suspend Company</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="text-muted-foreground">Disables logins by policy, automations, publishing, AI usage, and API access while keeping the company visible.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => runAction("suspend_company")}>Suspend Company</Button>
                  <Button variant="outline" onClick={() => runAction("reactivate_company")}>Reactivate</Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-destructive/40">
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Shield className="h-4 w-4" />Disable Company and All Users</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="text-muted-foreground">Disables company, all users, websites, automations, and integrations. Type DISABLE to continue.</p>
                <Input value={disableText} onChange={(event) => setDisableText(event.target.value)} placeholder="DISABLE" />
                <Button variant="destructive" disabled={disableText !== "DISABLE"} onClick={() => operation.mutate({ op: "disable_company_all_users", payload: { confirm_text: disableText } })}>Disable Entire Company</Button>
              </CardContent>
            </Card>

            <Card className="border-destructive">
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Trash2 className="h-4 w-4" />Delete Company Completely</CardTitle></CardHeader>
              <CardContent className="space-y-4 text-sm">
                <p className="font-medium text-destructive">This action permanently removes all company data and cannot be undone.</p>
                <StatsGrid stats={center.deletion_preview} />
                <Input value={deleteText} onChange={(event) => setDeleteText(event.target.value)} placeholder="DELETE" />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={secondConfirm} onChange={(event) => setSecondConfirm(event.target.checked)} />
                  I confirm this permanent deletion.
                </label>
                <Button
                  variant="destructive"
                  disabled={deleteText !== "DELETE" || !secondConfirm}
                  onClick={() => operation.mutate({ op: "delete_company_completely", payload: { confirm_text: deleteText, second_confirm: secondConfirm } })}
                >
                  Delete Company Completely
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <CompanyActionDialog
        action={pendingAction}
        center={center}
        form={actionForm}
        pending={operation.isPending}
        onChange={setActionForm}
        onClose={() => setPendingAction(null)}
        onSubmit={submitPendingAction}
      />
    </div>
  );
}

function InfoCard({ title, rows }: { title: string; rows: Array<[string, any]> }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{label}</span>
            <span className="max-w-[60%] truncate text-right font-medium">{String(value ?? "Unknown")}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function actionRequiresDialog(action: string) {
  return [
    "add_user", "remove_user", "disable_user", "enable_user", "reset_password", "force_logout", "resend_invitation", "transfer_ownership", "login_as_user", "view_user_activity",
    "upgrade_package", "downgrade_package", "grant_credits", "remove_credits", "override_limits",
    "create_website", "disable_website", "delete_website", "rebuild_website", "force_sync", "repair_configuration",
    "bulk_delete_articles", "bulk_publish_articles",
    "rerun_source", "disable_source", "delete_source",
    "test_connection", "reconnect",
    "edit_template", "clone_templates", "import_templates",
    "restore_backup", "duplicate_company", "clone_configuration", "clone_integrations",
  ].includes(action);
}

function createInitialActionForm(action: string, center: CompanyCenter): ActionFormState {
  const ownerId = String(center.company.owner_user_id ?? "");
  const firstUserId = String(center.users[0]?.user_id ?? ownerId);
  const firstWebsiteId = String(center.websites[0]?.id ?? "");
  const firstSourceId = String(center.sources[0]?.id ?? "");
  const firstTemplateId = String(center.templates.email[0]?.id ?? "");
  const firstProvider = String(center.integrations.providers.find((row: any) => row.status !== "not_configured")?.provider ?? "Stripe");
  return {
    ...emptyActionForm,
    user_id: firstUserId,
    invitation_id: action === "resend_invitation" ? String(center.invitations[0]?.id ?? "") : "",
    email: action === "resend_invitation" ? String(center.invitations[0]?.email ?? "") : String(center.company.contact_email ?? ""),
    package_id: String(center.company.subscription_plan_id ?? ""),
    limits_json: JSON.stringify(center.subscription?.manual_limit_overrides ?? { monthly_limit: 0, daily_limit: 0 }, null, 2),
    name: action === "duplicate_company" ? `${center.company.name ?? "Company"} Copy` : `${center.company.name ?? "Company"} Website`,
    website_id: firstWebsiteId,
    source_id: firstSourceId,
    provider: firstProvider,
    template_id: firstTemplateId,
  };
}

function parseActionJson(value: string, label: string) {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    toast.error(`${label} must be valid JSON`);
    return null;
  }
}

function buildActionPayload(action: string, form: ActionFormState): Record<string, unknown> | null {
  if (action === "add_user") {
    if (!form.email.trim()) return toastMissing("Email");
    return { email: form.email.trim(), role: form.role || "viewer" };
  }
  if (["remove_user", "disable_user", "enable_user", "force_logout", "transfer_ownership", "login_as_user", "view_user_activity"].includes(action)) {
    if (!form.user_id) return toastMissing("User");
    return { user_id: form.user_id };
  }
  if (action === "reset_password") {
    if (!form.user_id) return toastMissing("User");
    return { user_id: form.user_id, email: form.email.trim() || undefined };
  }
  if (action === "resend_invitation") {
    if (!form.invitation_id && !form.email.trim()) return toastMissing("Invitation");
    return { invitation_id: form.invitation_id || undefined, email: form.email.trim() || undefined };
  }
  if (["upgrade_package", "downgrade_package"].includes(action)) {
    if (!form.package_id.trim()) return toastMissing("Package ID");
    return { package_id: form.package_id.trim() };
  }
  if (["grant_credits", "remove_credits"].includes(action)) {
    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) return toastMissing("Valid credit amount");
    return { amount };
  }
  if (action === "override_limits") {
    const limits = parseActionJson(form.limits_json, "Limits");
    return limits ? { limits } : null;
  }
  if (action === "create_website") {
    if (!form.name.trim()) return toastMissing("Website name");
    return { name: form.name.trim(), domain: form.domain.trim() || undefined };
  }
  if (["disable_website", "delete_website", "rebuild_website", "force_sync", "repair_configuration"].includes(action)) {
    if (!form.website_id) return toastMissing("Website");
    return { website_id: form.website_id };
  }
  if (["bulk_delete_articles", "bulk_publish_articles"].includes(action)) {
    if (!form.article_selector.trim()) return toastMissing("Article selector");
    return form.article_selector.startsWith("status:")
      ? { status: form.article_selector.replace("status:", "").trim() }
      : { article_ids: form.article_selector };
  }
  if (["rerun_source", "disable_source", "delete_source"].includes(action)) {
    if (!form.source_id) return toastMissing("Source");
    return { source_id: form.source_id };
  }
  if (["test_connection", "reconnect"].includes(action)) {
    if (!form.provider.trim()) return toastMissing("Provider");
    return { provider: form.provider.trim() };
  }
  if (action === "edit_template") {
    if (!form.template_id) return toastMissing("Template");
    const fields = parseActionJson(form.fields_json, "Template fields");
    return fields ? { template_id: form.template_id, fields } : null;
  }
  if (["clone_templates", "clone_configuration", "clone_integrations"].includes(action)) {
    if (!form.source_company_id.trim()) return toastMissing("Source company ID");
    return { source_company_id: form.source_company_id.trim() };
  }
  if (action === "import_templates") {
    const templates = parseActionJson(form.templates_json, "Templates");
    return templates ? { templates } : null;
  }
  if (action === "restore_backup") {
    const backup = parseActionJson(form.backup_json, "Backup");
    return backup ? { backup } : null;
  }
  if (action === "duplicate_company") {
    if (!form.name.trim()) return toastMissing("Company name");
    return { name: form.name.trim() };
  }
  return {};
}

function toastMissing(label: string) {
  toast.error(`${label} is required`);
  return null;
}

function CompanyActionDialog({
  action,
  center,
  form,
  pending,
  onChange,
  onClose,
  onSubmit,
}: {
  action: string | null;
  center: CompanyCenter;
  form: ActionFormState;
  pending: boolean;
  onChange: (value: ActionFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const update = (patch: Partial<ActionFormState>) => onChange({ ...form, ...patch });
  const userActions = ["remove_user", "disable_user", "enable_user", "reset_password", "force_logout", "transfer_ownership", "login_as_user", "view_user_activity"];
  const websiteActions = ["disable_website", "delete_website", "rebuild_website", "force_sync", "repair_configuration"];
  const sourceActions = ["rerun_source", "disable_source", "delete_source"];
  const templateActions = ["edit_template"];
  const cloneActions = ["clone_templates", "clone_configuration", "clone_integrations"];
  const title = action ? titleCase(action) : "Company Action";
  const selectedUser = center.users.find((user) => user.user_id === form.user_id);

  return (
    <Dialog open={Boolean(action)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Configure the admin action for {center.company.name}. The request is validated server-side and written to audit logs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {action === "add_user" && (
            <>
              <Field label="Email">
                <Input value={form.email} onChange={(event) => update({ email: event.target.value })} placeholder="user@example.com" />
              </Field>
              <Field label="Role">
                <Select value={form.role} onValueChange={(role) => update({ role })}>
                  <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    {["viewer", "editor", "admin", "owner"].map((role) => <SelectItem key={role} value={role}>{titleCase(role)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}

          {action && userActions.includes(action) && (
            <Field label="User">
              <Select value={form.user_id} onValueChange={(user_id) => update({ user_id, email: center.users.find((user) => user.user_id === user_id)?.email ?? form.email })}>
                <SelectTrigger><SelectValue placeholder="Choose a user" /></SelectTrigger>
                <SelectContent>
                  {center.users.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.full_name || "Unknown"} - {user.email} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedUser && <p className="mt-2 text-xs text-muted-foreground">{selectedUser.email} - {selectedUser.status}</p>}
            </Field>
          )}

          {action === "reset_password" && (
            <Field label="Recovery email">
              <Input value={form.email} onChange={(event) => update({ email: event.target.value })} placeholder={selectedUser?.email ?? "user@example.com"} />
            </Field>
          )}

          {action === "resend_invitation" && (
            <Field label="Invitation email">
              <Select
                value={form.invitation_id || form.email}
                onValueChange={(value) => {
                  const invite = center.invitations.find((item) => item.id === value);
                  update(invite ? { invitation_id: invite.id, email: invite.email } : { invitation_id: "", email: value });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Choose an invitation" /></SelectTrigger>
                <SelectContent>
                  {center.invitations.filter((invite) => !invite.accepted_at).map((invite) => <SelectItem key={invite.id ?? invite.email} value={invite.id ?? invite.email}>{invite.email} ({invite.role})</SelectItem>)}
                </SelectContent>
              </Select>
              <Input className="mt-2" value={form.email} onChange={(event) => update({ invitation_id: "", email: event.target.value })} placeholder="or enter email" />
            </Field>
          )}

          {action && ["upgrade_package", "downgrade_package"].includes(action) && (
            <Field label="Package ID">
              <Input value={form.package_id} onChange={(event) => update({ package_id: event.target.value })} placeholder="Package UUID" />
            </Field>
          )}

          {action && ["grant_credits", "remove_credits"].includes(action) && (
            <Field label="Credit amount">
              <Input type="number" min="1" value={form.amount} onChange={(event) => update({ amount: event.target.value })} />
            </Field>
          )}

          {action === "override_limits" && (
            <Field label="Limits JSON">
              <Textarea value={form.limits_json} onChange={(event) => update({ limits_json: event.target.value })} className="font-mono text-xs" />
            </Field>
          )}

          {action === "create_website" && (
            <>
              <Field label="Website name">
                <Input value={form.name} onChange={(event) => update({ name: event.target.value })} />
              </Field>
              <Field label="Domain">
                <Input value={form.domain} onChange={(event) => update({ domain: event.target.value })} placeholder="optional" />
              </Field>
            </>
          )}

          {action && websiteActions.includes(action) && (
            <Field label="Website">
              <Select value={form.website_id} onValueChange={(website_id) => update({ website_id })}>
                <SelectTrigger><SelectValue placeholder="Choose a website" /></SelectTrigger>
                <SelectContent>
                  {center.websites.map((website) => <SelectItem key={website.id} value={website.id}>{website.name ?? website.domain ?? website.id} - {website.status}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          {action && ["bulk_delete_articles", "bulk_publish_articles"].includes(action) && (
            <Field label="Article selector">
              <Input value={form.article_selector} onChange={(event) => update({ article_selector: event.target.value })} placeholder="status:draft or comma-separated article IDs" />
            </Field>
          )}

          {action && sourceActions.includes(action) && (
            <Field label="Source">
              <Select value={form.source_id} onValueChange={(source_id) => update({ source_id })}>
                <SelectTrigger><SelectValue placeholder="Choose a source" /></SelectTrigger>
                <SelectContent>
                  {center.sources.map((source) => <SelectItem key={source.id} value={source.id}>{source.topic ?? source.feed_url ?? source.id} - {source.last_status ?? "unknown"}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          {action && ["test_connection", "reconnect"].includes(action) && (
            <Field label="Provider">
              <Select value={form.provider} onValueChange={(provider) => update({ provider })}>
                <SelectTrigger><SelectValue placeholder="Choose provider" /></SelectTrigger>
                <SelectContent>
                  {center.integrations.providers.map((integration: any) => <SelectItem key={integration.provider} value={integration.provider}>{integration.provider} - {integration.status}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          {action && templateActions.includes(action) && (
            <>
              <Field label="Email template">
                <Select value={form.template_id} onValueChange={(template_id) => update({ template_id })}>
                  <SelectTrigger><SelectValue placeholder="Choose template" /></SelectTrigger>
                  <SelectContent>
                    {center.templates.email.map((template) => <SelectItem key={template.id} value={template.id}>{template.name ?? template.template_key}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Fields JSON">
                <Textarea value={form.fields_json} onChange={(event) => update({ fields_json: event.target.value })} className="font-mono text-xs" />
              </Field>
            </>
          )}

          {action && cloneActions.includes(action) && (
            <Field label="Source company ID">
              <Input value={form.source_company_id} onChange={(event) => update({ source_company_id: event.target.value })} placeholder="Company UUID to clone from" />
            </Field>
          )}

          {action === "import_templates" && (
            <Field label="Templates JSON array">
              <Textarea value={form.templates_json} onChange={(event) => update({ templates_json: event.target.value })} className="font-mono text-xs" />
            </Field>
          )}

          {action === "restore_backup" && (
            <Field label="Backup JSON">
              <Textarea value={form.backup_json} onChange={(event) => update({ backup_json: event.target.value })} className="min-h-40 font-mono text-xs" />
            </Field>
          )}

          {action === "duplicate_company" && (
            <Field label="New company name">
              <Input value={form.name} onChange={(event) => update({ name: event.target.value })} />
            </Field>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button onClick={onSubmit} disabled={pending}>{pending ? "Running..." : title}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function ActionBar({ actions, onRun }: { actions: string[]; onRun: (action: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((action) => (
        <Button key={action} size="sm" variant="outline" onClick={() => onRun(action)}>
          {titleCase(action)}
        </Button>
      ))}
    </div>
  );
}

function DataTable({ title, rows, columns }: { title: string; rows: Array<Record<string, any>>; columns: string[] }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>{columns.map((column) => <TableHead key={column}>{titleCase(column)}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length} className="py-8 text-center text-sm text-muted-foreground">No records found.</TableCell></TableRow>
              ) : rows.map((row, index) => (
                <TableRow key={row.id ?? index}>
                  {columns.map((column) => <TableCell key={column} className="max-w-[260px] truncate text-sm">{formatValue(row[column])}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function PendingInvitationsTable({
  rows,
  pending,
  onResend,
  onRemove,
}: {
  rows: Array<Record<string, any>>;
  pending: boolean;
  onResend: (row: Record<string, any>) => void;
  onRemove: (row: Record<string, any>) => void;
}) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Pending Invitations</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {["email", "role", "created_at", "expires_at"].map((column) => <TableHead key={column}>{titleCase(column)}</TableHead>)}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No pending invitations found.</TableCell></TableRow>
              ) : rows.map((row, index) => (
                <TableRow key={row.id ?? index}>
                  {["email", "role", "created_at", "expires_at"].map((column) => <TableCell key={column} className="max-w-[260px] truncate text-sm">{formatValue(row[column])}</TableCell>)}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" disabled={pending} onClick={() => onResend(row)}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Resend
                      </Button>
                      <Button size="sm" variant="destructive" disabled={pending} onClick={() => onRemove(row)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function StatsGrid({ stats }: { stats: Record<string, any> }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {Object.entries(stats).map(([key, value]) => (
        <Card key={key}>
          <CardContent className="pt-5">
            <p className="text-[10px] font-medium uppercase text-muted-foreground">{titleCase(key)}</p>
            <p className="text-xl font-semibold">{formatValue(value)}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function HealthBadge({ level, score }: { level: string; score: number }) {
  const variant = level === "healthy" ? "default" : level === "critical" ? "destructive" : "secondary";
  return <Badge variant={variant as "default" | "secondary" | "destructive"}>{titleCase(level)} · {score}%</Badge>;
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === "active" ? "default" : status === "suspended" || status === "archived" ? "destructive" : "secondary";
  return <Badge variant={variant as "default" | "secondary" | "destructive"}>{titleCase(status)}</Badge>;
}

function countRole(users: Array<Record<string, any>>, role: string) {
  return users.filter((user) => user.role === role).length;
}

function formatDate(value?: string | null) {
  if (!value) return "Never";
  return new Date(value).toLocaleString();
}

function formatValue(value: any) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return new Intl.NumberFormat("fr-FR").format(value);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDate(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function titleCase(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function exportCsv(filename: string, rows: Array<Record<string, any>>) {
  const columns = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
  const csv = [columns.join(","), ...rows.map((row) => columns.map((column) => JSON.stringify(row[column] ?? "")).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function exportJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
